import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { UnauthQueryOptions } from '@/api';
import { Image } from '@/components/client';

const IMAGES = {
  goldMedal: 'https://cdn.welfarehello.com/static/gold_medal.png',
  silverMedal: 'https://cdn.welfarehello.com/static/silver_medal.png',
  bronzeMedal: 'https://cdn.welfarehello.com/static/bronze_medal.png',
};

export const PrizeSection = () => {
  const { data } = useSuspenseQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home-donation-event-history/invitee-donation/top'
    ]({
      query: {
        eventCode: 'DECEMBER_EVENT',
        size: 10,
      },
    }),
    select: ({ context }) => context?.contents,
  });

  // rank 1,2,3의 최대 너비를 저장하는 상태
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // rank 1,2,3 항목들의 너비를 측정하여 최대값을 구함
  useEffect(() => {
    if (!containerRef.current) return;

    const measureElements = () => {
      const topThreeElements = containerRef.current?.querySelectorAll(
        '[data-rank="1"] .name-price-container, [data-rank="2"] .name-price-container, [data-rank="3"] .name-price-container',
      );

      if (!topThreeElements || topThreeElements.length === 0) return;

      let maxElementWidth = 0;
      topThreeElements.forEach((element) => {
        const width = element.scrollWidth;
        if (width > maxElementWidth) {
          maxElementWidth = width + 4;
        }
      });

      setMaxWidth(maxElementWidth);
    };

    // 초기 측정
    measureElements();

    // 폰트 로딩 완료 후 재측정
    document.fonts.ready.then(measureElements);
  }, [data]);

  return (
    <div className="flex w-full flex-col" ref={containerRef}>
      <h2
        className="mb-20 text-center font-800 leading-[120%] text-Gray-white"
        style={{ fontSize: '32px' }}
      >
        실시간 친구 기부 완료
        <br />
        TOP 10
      </h2>
      <div className="flex flex-col gap-4">
        {/* 헤더 */}
        <div className="flex justify-between px-12 py-4 font-700 text-B-100 typo-body4">
          <p>실시간 순위</p>
          <p>닉네임</p>
          <p>친구 기부 횟수</p>
        </div>
        {data?.map((item, index) => {
          const rank = index + 1;
          const price = (() => {
            switch (rank) {
              case 1:
                return 200000;
              case 2:
                return 100000;
              case 3:
                return 50000;
              default:
                return undefined;
            }
          })();

          return (
            <RankingRow
              key={index}
              name={item.maskedNickname}
              rank={rank}
              count={item.donationCount}
              price={price}
              nicknameMinWidth={maxWidth}
            />
          );
        })}
      </div>
    </div>
  );
};

interface RankingRowProps {
  /** 이름 */
  name: string;
  /** 순위 */
  rank: number;
  /** 친구 기부 횟수 */
  count?: number;
  /** 금액 */
  price?: number;
  /** 닉네임 최소 너비 */
  nicknameMinWidth: number;
}

const RankingRow = ({
  count,
  name,
  rank,
  price,
  nicknameMinWidth,
}: RankingRowProps) => {
  const medalImage = (() => {
    switch (rank) {
      case 1:
        return <Image alt="goldMedal" fill sizes="30px" src={IMAGES.goldMedal} />;
      case 2:
        return <Image alt="silverMedal" fill sizes="30px" src={IMAGES.silverMedal} />;
      case 3:
        return <Image alt="bronzeMedal" fill sizes="30px" src={IMAGES.bronzeMedal} />;
      default:
        return null;
    }
  })();

  return (
    <div
      className="flex items-center justify-between rounded-pill bg-[#5cb8ff] px-30 py-12 text-Gray-white"
      data-rank={rank}
    >
      <div
        className={cn(
          'relative flex size-30 min-w-0 shrink-0 items-center justify-center gap-4',
          {
            'items-end': rank <= 3,
          },
        )}
      >
        <span className="relative z-10 font-700 typo-caption1">{rank}</span>
        {medalImage}
      </div>
      <div
        className="name-price-container flex min-w-0 items-center justify-between gap-8"
        style={{
          width:
            rank <= 3 && nicknameMinWidth > 0
              ? `${nicknameMinWidth}px`
              : 'auto',
        }}
      >
        <p className="truncate font-600 text-Gray-white typo-caption1">
          {name}님
        </p>
        {price ? (
          <p className="min-w-77 truncate rounded-b-pill rounded-e-pill bg-O-400 px-10 py-2 font-700 text-Y-50 typo-caption1">
            {price?.toLocaleString()}원
          </p>
        ) : null}
      </div>
      <p className="min-w-40 text-center font-700 typo-caption1">
        {rank <= 2 ? '?' : `${count}명`}
      </p>
    </div>
  );
};
